/** Delivery framing is independent of the AES-GCM rendition protocolVersion. */
export interface MediaTransportProfile {
  transportVersion: 2;
  partDurationMs: 200;
  gopDurationMs: 1000;
  /** Declared encoding budget (video + audio), used for conservative relay reservations. */
  mediaBytesPerSecond: number;
}
export interface PlayerTransportCapabilities {
  transportVersions: Array<1 | 2>;
  videoCodecs: Array<'h264' | 'av1'>;
  /** Private single-track publications only; never substitutes for a public video codec. */
  audioCodecs?: Array<'aac'>;
  /** `false`: a leaf that only consumes (e.g. a TV). It is never an origin fetcher or a provider. Absent means it may relay. */
  relay?: boolean;
}

export interface DeliveryAssignment {
  transportVersion: 2;
  generation: string;
  revision: number;
  originFetcher: boolean;
  primaryPeerId: string | null;
  backupPeerId: string | null;
  /** Reserved viewer connections. The engine separately reserves a slot for direct origin fallback. */
  neighbors: string[];
  probe: boolean;
}
export function acceptsDeliveryAssignment(value: unknown, generation: string, self: string, revision: number): value is DeliveryAssignment {
  if (!value || typeof value !== 'object') return false;
  const a = value as Partial<DeliveryAssignment>;
  if (a.transportVersion !== 2 || !generation || a.generation !== generation ||
      !Number.isSafeInteger(a.revision) || a.revision! <= revision || a.revision! < 1 ||
      typeof a.originFetcher !== 'boolean' || typeof a.probe !== 'boolean' || !Array.isArray(a.neighbors) || a.neighbors.length > 8) return false;
  const peer = (id: unknown): id is string => typeof id === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(id) && id !== self && id !== 'server';
  if (!a.neighbors.every(peer) || new Set(a.neighbors).size !== a.neighbors.length) return false;
  for (const provider of [a.primaryPeerId, a.backupPeerId]) {
    if (provider !== null && (!peer(provider) || !a.neighbors.includes(provider))) return false;
  }
  if (a.originFetcher && (a.primaryPeerId !== null || a.backupPeerId !== null || a.probe)) return false;
  if (a.backupPeerId !== null && (!a.primaryPeerId || a.backupPeerId === a.primaryPeerId)) return false;
  if (a.probe && a.primaryPeerId === null) return false;
  return true;
}
